import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { supabase, STOCK_VIEW } from "@/lib/supabase";
import { Search, Sparkles, Recycle } from "lucide-react";
import StockStatusBadge, { bestStatusFromRows } from "@/components/StockStatusBadge";

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "phones", label: "Phones" },
  { key: "laptops", label: "Laptops" },
  { key: "accessories", label: "Accessories" },
];

const TABS = [
  { key: "all", label: "All stock", icon: Search, desc: "Browse our entire inventory" },
  { key: "new", label: "New", icon: Sparkles, desc: "Brand new — sealed, warranty included" },
  { key: "used", label: "Second-hand", icon: Recycle, desc: "Refurbished by JAS — fully tested, 12-month warranty" },
];

export default function Shop() {
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stockBySku, setStockBySku] = useState({});  // { sku: bestStatus }
  const { format } = useCurrency();
  const cat = sp.get("cat") || "all";
  const cond = sp.get("cond") || "all";
  const q = sp.get("q") || "";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const params = {};
      if (cat !== "all") params.category = cat;
      if (cond !== "all") params.condition = cond;
      if (q) params.q = q;
      const { data } = await api.get("/products", { params });
      if (cancelled) return;
      setItems(data);
      setLoading(false);

      // Bulk-fetch live stock for all visible SKUs in one Supabase query
      const skus = [...new Set((data || []).map((p) => p.sku).filter(Boolean))];
      if (skus.length && supabase) {
        const { data: rows, error } = await supabase
          .from(STOCK_VIEW)
          .select("sku,stock_status,available_quantity")
          .in("sku", skus);
        if (cancelled || error) return;
        const grouped = {};
        for (const r of rows || []) {
          if (!r.sku) continue;
          // Force out_of_stock when nothing is actually available
          const effective = r.available_quantity === 0 ? "out_of_stock" : r.stock_status;
          (grouped[r.sku] ||= []).push({ stock_status: effective });
        }
        const summary = {};
        for (const sku of Object.keys(grouped)) summary[sku] = bestStatusFromRows(grouped[sku]);
        setStockBySku(summary);
      } else {
        setStockBySku({});
      }
    })();
    return () => { cancelled = true; };
  }, [cat, cond, q]);

  const set = (k, v) => {
    const next = new URLSearchParams(sp);
    if (v && v !== "all") next.set(k, v); else next.delete(k);
    setSp(next);
  };

  const activeTab = TABS.find((t) => t.key === cond) || TABS[0];

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="shop-page">
      <div className="overline text-[#1a2d5e]">Shop</div>
      <h1 className="font-display font-black text-4xl lg:text-5xl mt-2 text-[#0b1838] tracking-tighter">
        {cond === "new" ? "Brand new" : cond === "used" ? "Second-hand" : "All products"}
      </h1>
      <p className="mt-2 text-slate-600 max-w-xl">{activeTab.desc}</p>

      {/* CONDITION TABS — prominent */}
      <div className="mt-8 grid sm:grid-cols-3 gap-3" data-testid="shop-condition-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = cond === t.key;
          return (
            <button
              key={t.key}
              onClick={() => set("cond", t.key)}
              data-testid={`shop-tab-${t.key}`}
              className={`group flex items-center gap-3 p-5 rounded-xl border-2 transition-all text-left ${
                active
                  ? "border-[#1a2d5e] bg-[#1a2d5e] text-white shadow-lg"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-400 hover:-translate-y-0.5"
              }`}
            >
              <div className={`p-3 rounded-lg ${active ? "bg-white/15" : "bg-slate-50"}`}>
                <Icon size={22} className={active ? "text-[#aedfff]" : "text-[#1a2d5e]"} />
              </div>
              <div className="flex-1">
                <div className="font-display font-bold text-lg">{t.label}</div>
                <div className={`text-xs mt-0.5 ${active ? "text-slate-200" : "text-slate-500"}`}>{t.desc}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* SECONDARY FILTERS */}
      <div className="mt-6 flex flex-col md:flex-row gap-4 items-stretch md:items-center" data-testid="shop-filters">
        <div className="flex-1 flex items-center gap-2 border border-slate-300 rounded-lg px-4 bg-white">
          <Search size={18} className="text-slate-400" />
          <input data-testid="shop-search" type="text" placeholder="Search products…" defaultValue={q} onKeyDown={(e) => { if (e.key === "Enter") set("q", e.target.value); }} className="flex-1 py-3 outline-none text-sm" />
        </div>
        <select data-testid="shop-cat-select" value={cat} onChange={(e) => set("cat", e.target.value)} className="border border-slate-300 rounded-lg px-3 py-3 text-sm bg-white">
          {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </div>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="shop-grid">
        {loading ? (
          Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-80 bg-slate-100 rounded-xl animate-pulse" />)
        ) : items.length === 0 ? (
          <div className="col-span-full text-center py-20 text-slate-500" data-testid="shop-empty">No products match your filters.</div>
        ) : (
          items.map((p) => {
            const liveStatus = p.sku ? stockBySku[p.sku] : null;
            return (
              <Link key={p.id} to={`/product/${p.id}`} data-testid={`product-card-${p.id}`} className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:-translate-y-1 hover:shadow-lg hover:border-slate-300 transition-all">
                <div className="aspect-square bg-slate-50 overflow-hidden relative">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs">No image</div>
                  )}
                  {liveStatus && (
                    <div className="absolute top-3 left-3" data-testid={`product-card-stock-${p.id}`}>
                      <StockStatusBadge status={liveStatus} />
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <span className="overline text-[10px]">{p.category}</span>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded ${p.condition === "new" ? "bg-[#aedfff] text-[#0b1838]" : "bg-slate-100 text-slate-600"}`}>{p.condition === "used" ? "second-hand" : "new"}</span>
                  </div>
                  <div className="font-display font-bold text-lg mt-2 text-[#0b1838] line-clamp-2">{p.title}</div>
                  <div className="mt-3 font-display font-black text-2xl text-[#1a2d5e]">{format(p.price)}</div>
                </div>
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
}
